import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { getPatientById, getPatientHistory, getPatientAppointments } from "@/data/mockData";
import { WearableDataWidget } from "@/components/patient/WearableDataWidget";
import {
  Activity,
  Calendar,
  Clock,
  ChevronRight,
  FileText,
  Heart,
  Droplets,
  TrendingUp,
  CheckCircle2,
  AlertTriangle,
  Watch
} from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { it } from "date-fns/locale";

// Using patient ID 1 (Giuseppe Verdi) as current patient
const CURRENT_PATIENT_ID = "1";

export const PatientHomePage = () => {
  const navigate = useNavigate();
  const patient = getPatientById(CURRENT_PATIENT_ID);
  const history = getPatientHistory(CURRENT_PATIENT_ID);
  const appointments = getPatientAppointments(CURRENT_PATIENT_ID);
  
  const upcomingAppointments = appointments.filter(a => a.status === "scheduled");
  const recentExam = history[0];

  // Count alerts in recent exam
  const alertsCount = recentExam?.results.filter(r => r.status !== "normal").length || 0;

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-4xl mx-auto">
      {/* Welcome */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Ciao, {patient?.name.split(" ")[0]}!</h1>
          <p className="text-muted-foreground">Benvenuto nel tuo portale salute</p>
        </div>
        <Button
          variant="premium"
          onClick={() => navigate("/booking?from=citizen&patientId=" + CURRENT_PATIENT_ID)}
          data-testid="patient-new-booking-btn"
          className="gap-2"
        >
          <Calendar className="h-4 w-4" />
          Prenota una prestazione
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <FileText className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold">{history.length}</p>
              <p className="text-sm text-muted-foreground">Esami effettuati</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-info/10 flex items-center justify-center">
              <Calendar className="h-6 w-6 text-info" />
            </div>
            <div>
              <p className="text-2xl font-bold">{upcomingAppointments.length}</p>
              <p className="text-sm text-muted-foreground">Prossimi appuntamenti</p>
            </div>
          </CardContent>
        </Card>

        <Card className={cn(alertsCount > 0 && "border-warning/50")}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className={cn(
              "h-12 w-12 rounded-xl flex items-center justify-center",
              alertsCount > 0 ? "bg-warning/10" : "bg-success/10"
            )}>
              {alertsCount > 0 ? (
                <AlertTriangle className="h-6 w-6 text-warning" />
              ) : (
                <CheckCircle2 className="h-6 w-6 text-success" />
              )}
            </div>
            <div>
              <p className="text-2xl font-bold">{alertsCount}</p>
              <p className="text-sm text-muted-foreground">
                {alertsCount > 0 ? "Valori da monitorare" : "Tutto nella norma"}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Wearable Data Widget */}
      <WearableDataWidget compact />

      {/* Latest Exam Summary */}
      {recentExam && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-primary" />
                  Ultimo Controllo
                </CardTitle>
                <CardDescription>
                  {format(new Date(recentExam.date), "d MMMM yyyy", { locale: it })}
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate("/patient/history")}>
                Dettagli
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {recentExam.results.slice(0, 6).map((result, i) => (
                <div
                  key={i}
                  className={cn(
                    "p-3 rounded-lg border flex items-center justify-between",
                    result.status === "normal" && "bg-success/5 border-success/20",
                    result.status === "high" && "bg-warning/5 border-warning/20",
                    result.status === "low" && "bg-info/5 border-info/20"
                  )}
                >
                  <div>
                    <p className="text-sm font-medium">{result.name}</p>
                    <p className={cn(
                      "text-lg font-bold",
                      result.status === "normal" && "text-success",
                      result.status === "high" && "text-warning",
                      result.status === "low" && "text-info"
                    )}>
                      {result.value} <span className="text-sm font-normal">{result.unit}</span>
                    </p>
                  </div>
                  {result.status === "normal" ? (
                    <CheckCircle2 className="h-5 w-5 text-success" />
                  ) : result.status === "high" ? (
                    <TrendingUp className="h-5 w-5 text-warning" />
                  ) : (
                    <TrendingUp className="h-5 w-5 text-info rotate-180" />
                  )}
                </div>
              ))}
            </div>
            {recentExam.notes && (
              <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                <p className="text-sm">
                  <span className="font-medium">Note del medico:</span> {recentExam.notes}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Upcoming Appointments */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-info" />
              Prossimi Appuntamenti
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {upcomingAppointments.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">Nessun appuntamento programmato</p>
              <Button variant="link" className="mt-2" onClick={() => navigate("/booking?from=citizen&patientId=" + CURRENT_PATIENT_ID)} data-testid="patient-empty-book-btn">
                Prenota un controllo
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {upcomingAppointments.map((apt) => (
                <div
                  key={apt.id}
                  className="p-4 rounded-lg border bg-muted/30 flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground uppercase">
                        {format(new Date(apt.date), "MMM", { locale: it })}
                      </p>
                      <p className="text-2xl font-bold">
                        {format(new Date(apt.date), "d")}
                      </p>
                    </div>
                    <div>
                      <p className="font-medium flex items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        {apt.time}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {apt.services.join(", ")}
                      </p>
                    </div>
                  </div>
                  <Badge variant="secondary">Confermato</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Health Tips */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Heart className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold">Consiglio del giorno</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Ricorda di misurare la glicemia prima dei pasti principali e di annotare i valori.
                Un buon controllo glicemico aiuta a prevenire complicazioni a lungo termine.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PatientHomePage;
